import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { messageAPI, settingsAPI } from '../services/api';
import { Send, AlertCircle } from 'lucide-react';

const Messages = () => {
  const [messages, setMessages] = useState([]);
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [newPhoneNumber, setNewPhoneNumber] = useState('');
  const [messageText, setMessageText] = useState('');

  const conversations = useMemo(() => {
    const map = new Map();

    messages.forEach((msg) => {
      const key = msg.phoneNumber;
      const existing = map.get(key);
      const timestamp = new Date(msg.createdAt).getTime();

      if (!existing || timestamp > existing.lastAt) {
        map.set(key, {
          phoneNumber: msg.phoneNumber,
          waConfigId: msg.waConfigId,
          displayName: msg.phoneNumber,
          lastMessage: msg.message || (msg.type === 'incoming' ? 'Incoming message' : 'Outgoing message'),
          lastAt: timestamp,
          unread: msg.type === 'incoming' ? 1 : (existing?.unread || 0),
        });
      } else if (msg.type === 'incoming' && existing) {
        existing.unread = (existing.unread || 0) + 1;
      }
    });

    return Array.from(map.values()).sort((a, b) => b.lastAt - a.lastAt);
  }, [messages]);

  const selectedMessages = useMemo(() => {
    if (!selectedConversation) return [];
    return messages
      .filter((msg) => msg.phoneNumber === selectedConversation.phoneNumber)
      .sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
  }, [messages, selectedConversation]);

  const fetchData = useCallback(async () => {
    try {
      const [messagesRes, configsRes] = await Promise.all([
        messageAPI.getMessages({ page: 1, limit: 100 }),
        settingsAPI.getConfigurations(),
      ]);

      setMessages(messagesRes.data.messages);
      setConfigs(configsRes.data.configs);

      if (!selectedConversation && messagesRes.data.messages.length > 0) {
        const firstPhone = messagesRes.data.messages[0].phoneNumber;
        const firstMessage = messagesRes.data.messages.find((msg) => msg.phoneNumber === firstPhone);
        setSelectedConversation({
          phoneNumber: firstPhone,
          waConfigId: firstMessage?.waConfigId || configsRes.data.configs[0]?.id,
        });
        setNewPhoneNumber(firstPhone);
      }

      if (configsRes.data.configs.length > 0 && !selectedConversation) {
        setNewPhoneNumber('');
      }
    } catch (err) {
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [selectedConversation]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSelectConversation = (conversation) => {
    setSelectedConversation(conversation);
    setNewPhoneNumber(conversation.phoneNumber);
    setMessageText('');
    setError('');
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');

    const phoneNumber = selectedConversation?.phoneNumber || newPhoneNumber;
    const waConfigId = selectedConversation?.waConfigId || configs[0]?.id;

    if (!waConfigId || !phoneNumber || !messageText) {
      setError('Please select a conversation or enter a phone number, and type a message.');
      return;
    }

    try {
      await messageAPI.sendMessage({ waConfigId, phoneNumber, message: messageText });
      setSuccessMessage('Message sent successfully!');
      setMessageText('');
      fetchData();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to send message');
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-screen">Loading...</div>;
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <h1 className="text-3xl font-bold text-gray-900 mb-6">WhatsApp Chat</h1>

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded flex gap-3">
          <AlertCircle className="text-red-600" size={20} />
          <p className="text-red-800">{error}</p>
        </div>
      )}

      {successMessage && (
        <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded text-green-800">
          {successMessage}
        </div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-[320px_minmax(0,1fr)] gap-6">
        <div className="bg-white rounded-3xl shadow-lg overflow-hidden border border-gray-200">
          <div className="border-b border-gray-200 px-6 py-5 bg-gray-50">
            <h2 className="text-lg font-semibold text-gray-900">Chats</h2>
            <p className="text-sm text-gray-500">{conversations.length} active chats</p>
          </div>
          <div className="divide-y divide-gray-200 max-h-[calc(100vh-220px)] overflow-y-auto">
            {conversations.length === 0 ? (
              <div className="p-6 text-gray-600">No conversations yet.</div>
            ) : (
              conversations.map((conversation) => (
                <button
                  key={conversation.phoneNumber}
                  onClick={() => handleSelectConversation(conversation)}
                  className={`w-full text-left px-5 py-4 hover:bg-gray-50 transition ${
                    selectedConversation?.phoneNumber === conversation.phoneNumber ? 'bg-green-50' : ''
                  }`}
                >
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <p className="font-semibold text-gray-900">{conversation.displayName}</p>
                      <p className="text-sm text-gray-500">{conversation.lastMessage}</p>
                    </div>
                    <span className="text-xs text-gray-400">
                      {new Date(conversation.lastAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </span>
                  </div>
                </button>
              ))
            )}
          </div>
        </div>

        <div className="bg-white rounded-3xl shadow-lg border border-gray-200 flex flex-col overflow-hidden">
          <div className="border-b border-gray-200 px-6 py-5 bg-white">
            <div className="flex items-center justify-between gap-3">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">
                  {selectedConversation ? selectedConversation.phoneNumber : 'Select a chat'}
                </h2>
                <p className="text-sm text-gray-500">
                  {selectedConversation
                    ? 'Reply to this chat or send a new message.'
                    : 'Choose a conversation on the left or type a phone number below.'}
                </p>
              </div>
              <div className="text-right">
                <span className="inline-flex rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-600">
                  {selectedMessages.length} messages
                </span>
              </div>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto px-6 py-6 space-y-4 bg-gray-50">
            {selectedConversation ? (
              selectedMessages.map((msg) => (
                <div
                  key={msg.id}
                  className={`flex ${msg.type === 'outgoing' ? 'justify-end' : 'justify-start'}`}
                >
                  <div className={`max-w-[80%] rounded-3xl px-5 py-4 shadow-sm ${
                    msg.type === 'outgoing'
                      ? 'bg-green-600 text-white rounded-br-[6px] rounded-tl-[24px] rounded-tr-[24px] rounded-bl-[24px]'
                      : 'bg-white text-gray-900 rounded-bl-[6px] rounded-tr-[24px] rounded-tl-[24px] rounded-br-[24px]'
                  }`}>
                    <p className="whitespace-pre-wrap">{msg.message || '[Media]'}</p>
                    <p className="mt-2 text-xs text-gray-400 text-right">{new Date(msg.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</p>
                  </div>
                </div>
              ))
            ) : (
              <div className="h-full grid place-items-center text-center text-gray-500">
                <div>
                  <p className="font-semibold">No conversation selected</p>
                  <p className="mt-2 text-sm">Click a chat on the left or start a new chat below.</p>
                </div>
              </div>
            )}
          </div>

          <form onSubmit={handleSendMessage} className="border-t border-gray-200 px-6 py-5 bg-white">
            <div className="grid gap-4 lg:grid-cols-[1fr_auto]">
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Phone Number</label>
                  <input
                    type="text"
                    value={selectedConversation?.phoneNumber || newPhoneNumber}
                    onChange={(e) => {
                      setNewPhoneNumber(e.target.value);
                      setSelectedConversation(null);
                    }}
                    placeholder="e.g., +1234567890"
                    className="w-full rounded-2xl border border-gray-300 px-4 py-3 focus:border-green-500 focus:ring-2 focus:ring-green-100"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Message</label>
                  <textarea
                    value={messageText}
                    onChange={(e) => setMessageText(e.target.value)}
                    rows="4"
                    placeholder="Write a reply or new message..."
                    className="w-full rounded-2xl border border-gray-300 px-4 py-3 focus:border-green-500 focus:ring-2 focus:ring-green-100"
                  />
                </div>
              </div>

              <button
                type="submit"
                className="inline-flex h-full items-center justify-center rounded-2xl bg-green-600 px-5 py-4 text-white transition hover:bg-green-700"
              >
                <Send size={20} />
                <span className="ml-2">Send</span>
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Messages;
